import React, { useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { SiteGate } from '@/components/SiteGate';
import Layout from '@/components/layout/Layout';
import SignalHome from '@/signal/SignalHome';
import { SignalProvider } from '@/signal/SignalContext';
const SignalCatalog = lazy(() => import('@/signal/SignalCatalog'));
const SignalOffer = lazy(() => import('@/signal/SignalOffer'));
const SignalPricing = lazy(() => import('@/signal/SignalPricing'));
const SignalFinder = lazy(() => import('@/signal/SignalFinder'));
const SignalProcess = lazy(() => import('@/signal/SignalProcess'));
const SignalInquiry = lazy(() => import('@/signal/SignalInquiry'));
const AiCoaching = lazy(() => import('@/signal/AiCoaching'));
import NotFound from '@/pages/NotFound';
import AuthCallback from '@/pages/AuthCallback';
import SignInPage from '@/pages/SignInPage';
import AdminRoute from '@/components/auth/AdminRoute';
const Welcome = lazy(() => import('@/components/landing/GalaxyLanding').then((m) => ({ default: m.WelcomePage })));
// Route-level code splitting: heavy pages load on demand, shrinking the initial bundle.
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const About = lazy(() => import('@/pages/About'));
const Learn = lazy(() => import('@/pages/Learn'));
const Play = lazy(() => import('@/pages/Play'));
const Build = lazy(() => import('@/pages/Build'));
const Membership = lazy(() => import('@/pages/Membership'));
const Shop = lazy(() => import('@/pages/Shop'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const MyDownloads = lazy(() => import('@/pages/MyDownloads'));
const Log = lazy(() => import('@/pages/Log'));
const Library = lazy(() => import('@/pages/Library'));
const ResourceDetail = lazy(() => import('@/pages/ResourceDetail'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Academy = lazy(() => import('@/pages/Academy'));
const Desk = lazy(() => import('@/pages/Desk'));
const ClearanceSuccess = lazy(() => import('@/pages/ClearanceSuccess'));
const ClearanceCancel = lazy(() => import('@/pages/ClearanceCancel'));
const Admin = lazy(() => import('@/pages/Admin'));
const WildGalaxy = lazy(() => import('@/pages/WildGalaxy'));
const Roadmap = lazy(() => import('@/pages/Roadmap'));
const DeepfakeInvestigation = lazy(() => import('@/pages/DeepfakeInvestigation'));
const ChainOfCustody = lazy(() => import('@/pages/ChainOfCustody'));
const PromptCraftDrill = lazy(() => import('@/pages/PromptCraftDrill'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const Network = lazy(() => import('@/pages/Network'));
const Hub = lazy(() => import('@/pages/Hub'));
const HubSuccess = lazy(() => import('@/pages/HubSuccess'));
const Verify = lazy(() => import('@/pages/Verify'));
const VerifyHistory = lazy(() => import('@/pages/VerifyHistory'));
const WorkshopHome = lazy(() => import('@/workshop/WorkshopHome'));
const WorkshopView = lazy(() => import('@/workshop/WorkshopView'));
const VerifyKeys = lazy(() => import('@/pages/VerifyKeys'));
const VerifyCrypto = lazy(() => import('@/pages/VerifyCrypto'));
const VerifyDownload = lazy(() => import('@/pages/VerifyDownload'));
const VerifyPublic = lazy(() => import('@/pages/VerifyPublic'));
const LicenseReference = lazy(() => import('@/pages/LicenseReference'));
const Licensing = lazy(() => import('@/pages/Licensing'));
const WorkWithUs = lazy(() => import('@/pages/WorkWithUs'));
const AffiliatePortal = lazy(() => import('@/pages/AffiliatePortal'));
const ContractorPortal = lazy(() => import('@/pages/ContractorPortal'));
const LicensingSuccess = lazy(() => import('@/pages/LicensingSuccess'));
const RegistrySearch = lazy(() => import('@/pages/RegistrySearch'));
const AutomationCenter = lazy(() => import('@/pages/AutomationCenter'));
const WebDevelopment = lazy(() => import('@/pages/WebDevelopment'));
const RegistryModules = lazy(() => import('@/pages/RegistryModules'));
const DigitalModels = lazy(() => import('@/pages/DigitalModels'));
const AutomationSavings = lazy(() => import('@/pages/AutomationSavings'));
const PlanningKit = lazy(() => import('@/pages/PlanningKit'));
const ClientValue = lazy(() => import('@/pages/ClientValue'));
const ProposalView = lazy(() => import('@/pages/ProposalView'));
const ValueRecap = lazy(() => import('@/pages/ValueRecap'));
const ModelCertificate = lazy(() => import('@/pages/ModelCertificate'));
const FieldOffice = lazy(() => import('@/pages/FieldOffice'));
const ServerFarmLab = lazy(() => import('@/pages/labs/ServerFarmLab'));
const ClusterBuilder = lazy(() => import('@/pages/labs/ClusterBuilder'));
const CreatorSettings = lazy(() => import('@/pages/CreatorSettings'));
const CreatorProfile = lazy(() => import('@/pages/CreatorProfile'));
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { AuthProvider } from '@/context/AuthContext';
import { Toaster } from 'sonner';
import { SiteCopyProvider } from '@/context/SiteCopyContext';
import { trackPageView } from '@/lib/analytics';

function ScrollToTop() {
    const { pathname, search } = useLocation();
    const referralFired = React.useRef(false);
    const previousPath = React.useRef(null);
    useEffect(() => {
        if (previousPath.current !== pathname) window.scrollTo({ top: 0, behavior: 'instant' });
        previousPath.current = pathname;
        trackPageView(pathname);
        // Fire ruling-share referral once per session on first visit that carries ?ref=ruling-share
        if (!referralFired.current) {
            try {
                const params = new URLSearchParams(search);
                const ref = params.get('ref');
                if (ref === 'ruling-share' || ref === 'case-share') {
                    referralFired.current = true;
                    const game = params.get('game') || (ref === 'ruling-share' ? 'coc' : null);
                    const caseNum = params.get('case') || params.get('drill') || null;
                    // eslint-disable-next-line no-unused-expressions
                    import('@/lib/analytics').then(({ trackEvent }) => {
                        // Unified event — always fire.
                        trackEvent('case_share_referral_visit', {
                            landing_path: pathname,
                            game,
                            case: caseNum,
                            source: ref,
                        });
                        // Legacy event for `ruling-share` links out in the wild.
                        if (ref === 'ruling-share') {
                            trackEvent('ruling_share_referral_visit', { landing_path: pathname, case: caseNum });
                        }
                    });
                }
            } catch { /* ignore */ }
        }
    }, [pathname, search]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    // Synchronous session_id detection — must run BEFORE ProtectedRoute checks kick in.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    return (
        <>
            <ScrollToTop />
            <Toaster theme="dark" position="bottom-center" richColors />
            <Layout>
                <Suspense fallback={null}>
                <Routes>
                    <Route path="/" element={<SignalHome />} />
                    <Route path="/pricing" element={<SignalPricing />} />
                    <Route path="/find-automation" element={<SignalFinder />} />
                    <Route path="/how-it-works" element={<SignalProcess />} />
                    <Route path="/inquiry" element={<SignalInquiry />} />
                    <Route path="/ai-coaching" element={<AiCoaching />} />
                    <Route path="/learn" element={<Learn />} />
                    <Route path="/academy" element={<Academy />} />
                    <Route path="/play" element={<Play />} />
                    <Route path="/modules" element={<Play />} />
                    <Route path="/modules/wild-galaxy" element={<WildGalaxy />} />
                    <Route path="/build" element={<Build />} />
                    <Route path="/community" element={<Navigate to="/network" replace />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/shop/:slug" element={<ProductDetail />} />
                    <Route path="/account/downloads" element={<MyDownloads />} />
                    <Route path="/log" element={<Log />} />
                    <Route path="/library" element={<Library />} />
                    <Route path="/library/:slug" element={<ResourceDetail />} />
                    <Route path="/membership" element={<Membership />} />
                    <Route path="/clearance/success" element={<ClearanceSuccess />} />
                    <Route path="/clearance/cancel" element={<ClearanceCancel />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/roadmap" element={<Roadmap />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/signin" element={<SignInPage />} />
                    <Route path="/auth" element={<Navigate to="/signin" replace />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/modules/deepfake-investigation" element={<DeepfakeInvestigation />} />
                    <Route path="/modules/chain-of-custody" element={<ChainOfCustody />} />
                    <Route path="/modules/prompt-craft-drill" element={<PromptCraftDrill />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/network/*" element={<Network />} />
                    <Route path="/automation-center" element={<SignalCatalog />} />
                    <Route path="/automation-center/configure" element={<AutomationCenter />} />
                    <Route path="/automation-center/:slug" element={<SignalOffer />} />
                    <Route path="/web-development" element={<WebDevelopment />} />
                    <Route path="/registry/modules" element={<RegistryModules />} />
                    <Route path="/digital-models" element={<DigitalModels />} />
                    <Route path="/automation-savings" element={<AutomationSavings />} />
                    <Route path="/resources/automation-planning-kit" element={<PlanningKit />} />
                    <Route path="/app/value" element={<ClientValue />} />
                    <Route path="/app/value/recap" element={<ValueRecap />} />
                    <Route path="/proposal/:token" element={<ProposalView />} />
                    <Route path="/models/certificate/:modelId" element={<ModelCertificate />} />
                    <Route path="/play/field-office" element={<FieldOffice />} />
                    <Route path="/automation-center/services" element={<Hub />} />
                    <Route path="/hub" element={<Navigate to="/automation-center" replace />} />
                    <Route path="/hub/success" element={<HubSuccess />} />
                    <Route path="/verify" element={<Verify />} />
                    <Route path="/verify/history" element={<VerifyHistory />} />
                    <Route path="/workshop" element={<WorkshopHome />} />
                    <Route path="/workshop/:workshopId" element={<WorkshopView />} />
                    <Route path="/workshop/:workshopId/room/:roomId" element={<WorkshopView />} />
                    <Route path="/verify/keys" element={<VerifyKeys />} />
                    <Route path="/verify/crypto" element={<VerifyCrypto />} />
                    <Route path="/shop/verify-download" element={<VerifyDownload />} />
                    <Route path="/verify/licenses" element={<LicenseReference />} />
                    <Route path="/licensing" element={<Licensing />} />
                    <Route path="/work-with-us" element={<WorkWithUs />} />
                    <Route path="/affiliate-portal" element={<AffiliatePortal />} />
                    <Route path="/contractor-portal" element={<ContractorPortal />} />
                    <Route path="/licensing/success" element={<LicensingSuccess />} />
                    <Route path="/verify/registry" element={<RegistrySearch />} />
                    <Route path="/automation" element={<Navigate to="/automation-center" replace />} />
                    <Route path="/labs/server-farm" element={<ServerFarmLab />} />
                    <Route path="/labs/server-farm/module/:slug" element={<ServerFarmLab />} />
                    <Route path="/labs/server-farm/build" element={<ClusterBuilder />} />
                    <Route path="/labs/server-farm/build/:buildId" element={<ClusterBuilder />} />
                    <Route path="/verify/r/:slug" element={<VerifyPublic />} />
                    <Route path="/creators/settings" element={<CreatorSettings />} />
                    <Route path="/creators/:handleWithAt" element={<CreatorProfile />} />
                    <Route
                        path="/desk"
                        element={
                            <ProtectedRoute>
                                <Desk />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <AdminRoute>
                                <Admin />
                            </AdminRoute>
                        }
                    />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
            </Layout>
        </>
    );
}

function App() {
    return (
        <SiteGate>
            <BrowserRouter>
                <AuthProvider>
                    <SiteCopyProvider>
                        <SignalProvider><AppRouter /></SignalProvider>
                    </SiteCopyProvider>
                </AuthProvider>
            </BrowserRouter>
        </SiteGate>
    );
}

export default App;
